/**
 * CommentLayer — renders comment/annotation pins on the canvas.
 *
 * Pins live in FLOW coordinates (a free anchor, or the live position of the
 * node a thread is pinned to), so they mean the same diagram point regardless
 * of each viewer's pan/zoom. Like PresenceCursors/HelperLines, this reads the
 * viewport transform [x, y, zoom] from the React Flow store and converts
 * flow → screen so pins track panning and zooming.
 *
 * The overlay container is non-interactive (pointerEvents: none) so it never
 * blocks canvas interaction; individual pins re-enable pointer events so they
 * can be clicked to focus their thread.
 *
 * Rendered INSIDE <ReactFlow> (needs ReactFlowProvider context).
 */
import { useStore } from '@xyflow/react'
import { MessageSquare, Check } from 'lucide-react'
import type { DiagramComment, Position } from '../types/schema'

/** Minimal node geometry a pin needs to anchor to a node (flow coords). */
export interface PinNodeGeometry {
  /** Center of the node in flow coordinates. */
  center: Position
  width: number
  height: number
}

/**
 * Resolve a comment's flow-coordinate pin location. A node-anchored thread pins
 * to the node's top-right corner (with `anchor` acting as an offset); a free
 * thread uses its `anchor` directly. Returns null when neither anchor resolves.
 *
 * Pure so the placement math is unit-testable without a DOM/React Flow render.
 */
export function resolvePinFlowPosition(
  comment: DiagramComment,
  nodeGeometry?: PinNodeGeometry | null,
): Position | null {
  if (comment.nodeId && nodeGeometry) {
    return {
      x: nodeGeometry.center.x + nodeGeometry.width / 2 + (comment.anchor?.x ?? 0),
      y: nodeGeometry.center.y - nodeGeometry.height / 2 + (comment.anchor?.y ?? 0),
    }
  }
  if (comment.anchor) return { x: comment.anchor.x, y: comment.anchor.y }
  return null
}

/** Convert a flow-space point to screen pixels using the viewport transform. */
export function flowToScreen(
  flow: Position,
  transform: [number, number, number],
): Position {
  const [tx, ty, zoom] = transform
  return { x: flow.x * zoom + tx, y: flow.y * zoom + ty }
}

export interface CommentLayerProps {
  comments: DiagramComment[]
  /** Currently-focused thread id (rendered emphasized). */
  activeCommentId?: string | null
  /** Hide resolved threads' pins when false. */
  showResolved?: boolean
  onPinClick?: (id: string) => void
}

export default function CommentLayer({
  comments,
  activeCommentId,
  showResolved = true,
  onPinClick,
}: CommentLayerProps) {
  const transform = useStore((s) => s.transform)
  // node lookup for node-anchored pins; subscribe so pins follow node drags.
  const nodeLookup = useStore((s) => s.nodeLookup)

  const placed = comments
    .filter((c) => (showResolved ? true : !c.resolved))
    .map((c) => {
      let geometry: PinNodeGeometry | null = null
      if (c.nodeId) {
        const internal = nodeLookup.get(c.nodeId)
        if (internal) {
          // nodeOrigin is [0.5, 0.5], so positionAbsolute is the node center.
          geometry = {
            center: internal.internals.positionAbsolute,
            width: internal.measured?.width ?? 0,
            height: internal.measured?.height ?? 0,
          }
        }
      }
      const flow = resolvePinFlowPosition(c, geometry)
      return flow ? { c, flow } : null
    })
    .filter((p): p is { c: DiagramComment; flow: Position } => p !== null)

  if (placed.length === 0) return null

  return (
    <div
      data-comment-layer
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 900,
        overflow: 'hidden',
      }}
    >
      {placed.map(({ c, flow }) => {
        const { x: screenX, y: screenY } = flowToScreen(flow, transform)
        const isActive = c.id === activeCommentId
        const replyCount = c.replies?.length ?? 0
        return (
          <button
            type="button"
            key={c.id}
            data-comment-pin={c.id}
            title={`${c.author}: ${c.text}`}
            onClick={() => onPinClick?.(c.id)}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              transform: `translate(${screenX}px, ${screenY - 28}px)`,
              pointerEvents: 'auto',
              willChange: 'transform',
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              padding: '3px 7px 3px 5px',
              border: isActive ? '2px solid #2563eb' : '1px solid rgba(0,0,0,0.15)',
              borderRadius: '12px 12px 12px 2px',
              background: c.resolved ? '#9ca3af' : '#f59e0b',
              color: '#fff',
              fontSize: 11,
              lineHeight: 1,
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              opacity: c.resolved ? 0.7 : 1,
            }}
          >
            {c.resolved ? <Check size={12} /> : <MessageSquare size={12} />}
            {replyCount > 0 && <span>{replyCount + 1}</span>}
          </button>
        )
      })}
    </div>
  )
}
